<template>
  <v-container id="canvas" fluid class="container-page">
    <MultiSplitPane
      split="horizontal"
      height=""
      resizerWidth="5px"
      classes="custom-resizer"
      :nested="true"
    >
      <Pane id="panel-left" :initWidth="'100%'">
        <template v-slot:content>
          <div
            class="m-l-12 m-t-12 m-r-12 d-flex v-middle text-title-container p-l-0 p-r-0"
          >
            <div class="text-title text-primary">
              <router-link to="/flows" class="link">Flows</router-link>
              <v-icon color="primary" class="mx-2">mdi-chevron-right</v-icon>
              {{ flowName }}
            </div>

            <v-spacer></v-spacer>
            <div v-if="loadingComplete" class="drawflow-action">
              <!-- call to flow -->
              <v-tooltip top v-if="flowPlatform === 'voice'">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-if="dialIsAllow"
                    v-bind="attrs"
                    v-on="on"
                    size="18"
                    class="outline m-l-5"
                    :disabled="!isEnableCall"
                    @click="callToFlow"
                  >
                    mdi-phone-return
                  </v-icon>
                </template>
                <span>Test call</span>
              </v-tooltip>

              <!-- save -->
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    class="btn-save m-l-5"
                    @click="save()"
                    :loading="btnSaveLoading"
                  >
                    <v-icon>fa-save</v-icon>
                  </v-btn>
                </template>
                <span>Save</span>
              </v-tooltip>

              <!-- options -->
              <v-menu :absolute="true">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    id="icon-dots"
                    class="p-l-15"
                    v-bind="attrs"
                    v-on="on"
                  >
                    fa-ellipsis-v
                  </v-icon>
                </template>
                <v-list dense>
                  <v-list-item
                    v-for="(option, index) in options"
                    :key="index"
                    :class="option.divider ? 'menu-divider' : ''"
                  >
                    <v-list-item-content v-if="option.divider">
                      <hr />
                    </v-list-item-content>
                    <v-list-item-content
                      v-else
                      class="pointer"
                      @click="optionClick(option.action)"
                    >
                      <v-list-item-subtitle
                        :id="`list${index}`"
                        class="font-size-body"
                      >
                        {{ option.title }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
          <MultiSplitPane
            split="horizontal"
            height=""
            resizerWidth="5px"
            classes="drawflow-custom-resizer"
          >
            <Pane id="panel-drawflow-left" :initWidth="'250px'">
              <template v-slot:content>
                <div class="tool-page m-l-12">
                  <component-tool-page
                    :ui="drawflowUi"
                    :tabs="tabs"
                    :structure="structure"
                    :tabSelected="tabSelected"
                    :nodeSelected="dataNodeSelected"
                    :isEnableBackward="isEnableBackward"
                    :isEnableForward="isEnableForward"
                    @backward="backward"
                    @forward="forward"
                    @setupStructure="setupStructure"
                    @componentClick="componentClick"
                    @pageClick="pageClick"
                    @pageCreate="pageCreate"
                    @pageCloneFromTemplate="pageCloneFromTemplate"
                    @pageSaveAsTemplate="pageSaveAsTemplate"
                    @pageConfirmRemove="pageConfirmRemove"
                    @pageRename="pageRename"
                  ></component-tool-page>
                </div>
              </template>
            </Pane>
            <Pane id="panel-drawflow-right" :initWidth="'100%'">
              <template v-slot:content>
                <div class="tool-drawflow m-l-5">
                  <div class="border">
                    <b>{{ tabSelected }}</b>
                  </div>
                  <div
                    id="drawflow"
                    @drop="drop"
                    @dragover="allowDrop"
                    @keydown.ctrl.67="copyNode"
                    @keydown.meta.67="copyNode"
                    @keydown.ctrl.86="parseNode"
                    @keydown.meta.86="parseNode"
                  >
                    <div class="bar-lock">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            @click="setPageLock(!isLocked)"
                            class="m-l-5"
                            :class="isLocked ? 'locked' : ''"
                          >
                            {{ isLocked ? 'fa-lock' : 'fa-lock-open' }}
                          </v-icon>
                        </template>
                        <span>{{
                          isLocked ? 'Page is locked' : 'Page is unlocked'
                        }}</span>
                      </v-tooltip>
                    </div>
                    <div class="bar-undo-redo">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            :disabled="!isEnableUndo || isLocked"
                            @click="undo"
                          >
                            fa-undo
                          </v-icon>
                        </template>
                        <span>Undo on this page</span>
                      </v-tooltip>

                      <!-- redo -->
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            class="outline m-l-5"
                            :disabled="!isEnableRedo || isLocked"
                            @click="redo"
                          >
                            fa-redo
                          </v-icon>
                        </template>
                        <span>Redo on this page</span>
                      </v-tooltip>
                    </div>
                    <div class="bar-zoom">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            @click="editor.zoom_out()"
                            class="m-l-5"
                          >
                            fa-search-minus
                          </v-icon>
                        </template>
                        <span>Zoom out</span>
                      </v-tooltip>

                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            @click="editor.zoom_reset()"
                            class="m-l-5"
                          >
                            fa-search
                          </v-icon>
                        </template>
                        <span>Reset zoom</span>
                      </v-tooltip>

                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            @click="editor.zoom_in()"
                          >
                            fa-search-plus
                          </v-icon>
                        </template>
                        <span>Zoom in</span>
                      </v-tooltip>
                    </div>

                    <component-tool-item
                      :componentItems="componentItems"
                      @dragstart="drag"
                    ></component-tool-item>
                  </div>
                </div>
              </template>
            </Pane>
          </MultiSplitPane>
          <!-- <div class="d-flex tool-group">
           
          </div> -->

          <div
            class="detail"
            :class="isShowDetail ? 'detail-show' : 'detail-hide'"
          >
            <detail-dialog
              v-if="isShowDetail"
              :node="dataNodeSelected"
              :tabs="tabs"
              :items="items"
              :ttsChoices="ttsChoices"
              :isNameDuplicated="isNameDuplicated"
              :currentTab="tabSelected"
              :drawFlowItems="drawFlowItems"
              :flowId="flowId"
              :errorFromValidate="errorFromValidate"
              :apiForIntellisense="apiForIntellisense"
              @goToPage="goToPage"
            ></detail-dialog>
            <!-- มีเพิ่ม apiForIntellisense ใช้สำหรับ intellisense code -->
          </div>
        </template>
      </Pane>
      <Pane id="panel-right" :initWidth="'0%'">
        <template v-slot:content>
          <Conversation
            :isOpen="dialConversation.isOpen"
            :botSessionId="dialConversation.botSessionId"
            :chatDetail="dialConversation.chatDetail"
            @close="hideConversation"
            class="m-t-12"
          />
        </template>
      </Pane>
    </MultiSplitPane>

    <dialog-version-history
      v-if="dialogVersionHistoryIsOpen"
      :isOpen="dialogVersionHistoryIsOpen"
      :flowId="flowId"
      @confirmRestore="confirmRestore"
      @dialogClose="closeVersionHistory"
    />

    <dialog-confirm
      v-if="dialogConfirm.open"
      :dialogConfirm="dialogConfirm"
      @dialogConfirmClick="confirmAction"
    />

    <snackbar v-if="snackbar.open" :snackbar="snackbar" />

    <v-tooltip
      top
      v-model="tooltip.active"
      :position-x="tooltip.positionX"
      :position-y="tooltip.positionY"
      :max-width="400"
    >
      <span class="overflow-wrap">{{ tooltip.text }}</span>
    </v-tooltip>

    <dialog-template
      v-if="dialogTemplate.open"
      :isOpen="dialogTemplate.open"
      :data="dialogTemplate.data"
      :tabs="tabs"
      :errorMessage="errorMessage"
      @save="addTemplate"
      @close="closeDialogTemplate"
      @delete="confirmDeleteTemplate"
    />
    <dialog-template-save
      v-if="dialogTemplateSaveIsOpen"
      :isOpen="dialogTemplateSaveIsOpen"
      :editedItem="editedItemTemplate"
      :errorMessage="errorMessage"
      @save="onClickSaveTemplate"
      @close="closeDialogSaveTemplate"
    />
    <dialog-call v-if="dialogCall.open" :dialogCall="dialogCall" />

    <dialog-import
      v-if="dialogImportIsOpen"
      :isOpen="dialogImportIsOpen"
      :errorMessage="errorMessage"
      @saveImport="importFlow"
    />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Vue from 'vue'
import Drawflow from 'drawflow'
import styleDrawflow from 'drawflow/dist/drawflow.min.css'
import _ from 'lodash'
import moment from 'moment'
import { MultiSplitPane, Pane } from 'vue-multi-split-pane'

import BotnoiIcon from '@/assets/botnoi.png'
import GoogleIcon from '@/assets/google-cloud.png'
import ReadSpeakerIcon from '@/assets/readspeaker.png'
import AzureIcon from '@/assets/azure.png'

import apiFlow from '@/apis/flow'
import apiFlowLog from '@/apis/flow_log'
import apiFlowTemplate from '@/apis/flow_template'
import apiSynthesis from '@/apis/synthesis'
import apiConversation from '@/apis/conversation'

import bus from './utils/event-bus'

import DialogConfirm from './components/dialog_confirm'
import DialogVersionHistory from './components/dialog_version_history'
import DialogImport from '../components/dialog_import'
import Snackbar from './components/snackbar'
import ComponentNormal from './components/normal'
import ComponentToolItem from './components/tool_item'
import ComponentToolPage from './components/tool_page'

import DetailDialog from './dialogs/index'
import DialogTemplate from './components/dialog_template'
import DialogTemplateSave from './components/dialog_template_save'
import DialogCall from '../components/dialog_call'

// items
import voiceItems from './items/voice'
import chatItems from './items/chat'

import DataMapping from './utils/data_mapping'

import Transform from './utils/transform'
import Arrange from './utils/arrange'
//=======add for integention
import apiField from '@/apis/field'
import apiNlu from '@/apis/nlu'
//=======add for integention

import Conversation from './components/conversation'

export default {
  props: {
    rulesCharcacters50: Object
  },
  components: {
    DetailDialog,
    DialogConfirm,
    DialogVersionHistory,
    DialogTemplateSave,
    DialogTemplate,
    DialogCall,
    DialogImport,
    Snackbar,
    MultiSplitPane,
    Pane,
    Conversation,
    ComponentToolItem,
    ComponentToolPage
  },
  data() {
    return {
      drawflowUi: null,
      editor: null,
      flowId: '',
      flowName: '',
      flowPlatform: '',
      flowVersion: 0,
      options: [
        { title: 'Version history', action: 'versionHistory' },
        { divider: true },
        { title: 'Import flow', action: 'import' },
        { title: 'Export flow', action: 'export' },
        { title: 'Clear all pages', action: 'clearAll' }
      ],
      pageOptions: [
        {
          title: 'Normal page',
          icon: 'far fa-file',
          action: 'normal'
        },
        {
          title: 'Terminal page',
          icon: 'fa-file',
          action: 'terminal'
        }
      ],
      tabs: [{ id: 1, name: 'Home' }],
      tabsSort: [],
      tabsSortAndFilter: [],
      tabsMenuWord: '',
      isLocked: false,
      drawFlowItems: {},
      items: [],
      componentItems: [],
      componentVoiceItems: [...voiceItems],
      componentChatItems: [...chatItems],
      tabSelected: 'Home',
      tabCurrentRename: '',
      structure: [],

      isEnableCall: false,
      isEnableUndo: false,
      isEnableRedo: false,
      isEnableBackward: false,
      isEnableForward: false,
      flowIndex: {},
      flowHistory: {},
      flowHistoryLimit: 30,
      pageIndex: -1,
      pageHistory: [],

      isShowDetail: false,
      isNameDuplicated: '',
      errorFromValidate: '',
      mobileItemSelect: {},
      mobileLastMove: null,
      dataNodeSelected: {},

      dialogVersionHistoryIsOpen: false,
      dialogConfirm: {
        action: '',
        open: false,
        name: '',
        message: '',
        nodes: []
      },
      snackbar: {
        open: false,
        colorSnackbar: '',
        textSnackbar: ''
      },
      isDragging: false,
      btnSaveLoading: false,
      ttsChoices: [],
      editedItem: {
        _id: '',
        name: '',
        platform: 'voice',
        description: ''
      },
      defaultItem: {
        _id: '',
        name: '',
        platform: 'voice',
        description: ''
      },
      errorMessage: '',
      tooltip: {
        active: false,
        text: '',
        positionX: 0,
        positionY: 0
      },
      dialogPage: false,
      tooltipTimeout: null,
      //======== add intellisense code
      apiForIntellisense: {},
      //======== add intellisense code

      // start flow template
      templatePageOptions: [
        {
          title: 'Blank page',
          // icon: 'far fa-file',
          action: 'normal'
        },
        {
          title: 'From a template',
          // icon: 'fa-file',
          action: 'template'
        }
      ],
      tabNameOptions: [
        {
          title: 'Delete',
          // icon: 'far fa-file',
          action: 'delete'
        },
        {
          title: 'Save as template',
          // icon: 'fa-file',
          action: 'save'
        }
      ],
      editedItemTemplate: {
        id: -1,
        name: '',
        oldName: ''
      },
      defaultItemTemplate: {
        id: -1,
        name: '',
        oldName: ''
      },
      dialogTemplate: {
        open: false,
        data: {}
      },
      dialogTemplateSaveIsOpen: false,
      // end flow template
      dialogCall: {
        from: 'flow-builder',
        open: false,
        _id: '',
        name: '',
        errorMessage: ''
      },
      nodesSelected: [],
      last_x: 0,
      last_y: 0,
      copyNodes: { page: '', elements: {} },
      dialConversation: {
        isOpen: false,
        botSessionId: '',
        chatCaller: {},
        chatDetail: {},
        reload: null
      },
      tempFlowUi: {
        tabs: [],
        ui: {}
      },
      loadingComplete: false,
      dialogImportIsOpen: false
    }
  },
  computed: {
    ...mapGetters('call', ['dialIsAllow', 'dialIsCalling', 'dialBotSessionId']),
    ...mapGetters('auth', ['user']),

    dragOptions() {
      return {
        animation: 0,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
        filter: '.no-drag',
        preventOnFilter: false
      }
    },
    rules() {
      return {
        rulesCharcacters50: this.rulesCharcacters50
      }
    }
  },
  watch: {
    tabs: {
      immediately: true,
      handler() {
        this.tabsSort = _.orderBy(
          this.tabs,
          [tab => tab.name.toLowerCase()],
          ['asc']
        )
      }
    },
    dialogPage: {
      handler(val) {
        if (val) {
          this.searchTabsMenuWord()

          setTimeout(async () => {
            const id = document.getElementById('search-page')
            id.focus()
          }, 200)
        }
      }
    },
    tabsMenuWord: {
      handler() {
        this.searchTabsMenuWord()
      }
    },
    dialIsCalling: {
      immediate: true,
      handler(val) {
        if (val) {
          this.isEnableCall = false
        } else {
          if (this.dialIsAllow) this.isEnableCall = true
        }
      }
    },
    dialBotSessionId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.dialConversation.botSessionId = this.dialBotSessionId
          this.dialConversation.isOpen = true
          this.showConversation()
        }
      }
    }
  },
  created() {
    document.title = this.$t('page-title.flow', {
      productName: this.$options.filters.getVariable('product_name')
    })

    window.addEventListener('beforeunload', this.beforeWindowUnload)
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload)
  },
  destroyed() {
    if (this.dialConversation.reload)
      clearInterval(this.dialConversation.reload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.checkSavedFlow()) {
      next(false)
      window.location = to.path // this is the trick
    } else {
      next()
    }
  },
  async mounted() {
    this.flowId = this.$route.params.id

    this.editorStart()
    // add flow history when has an new event
    const events = [
      'nodeCreated',
      'nodeRemoved',
      'nodeMoved',
      'connectionCreated',
      'connectionRemoved'
    ]
    events.forEach(evt => {
      this.editor.on(evt, id => {
        if (evt === 'nodeCreated' || evt === 'nodeRemoved') {
          this.getAllComponentItemsInTabs()
        }

        this.pushFlowHistory()
      })
    })

    // this.editor.on('nodeSelected', id => {
    //   const data = this.editor.getNodeFromId(id)
    //   if (data) this.dataNodeSelected = data
    //   else this.dataNodeSelected = {}
    // })

    this.editor.on('connectionCreated', connection => {
      const { output_id, input_id, output_class, input_class } = connection

      const start = this.editor.getNodeFromId(output_id)
      const end = this.editor.getNodeFromId(input_id)

      if (!start || !end) return

      // one output
      for (const output in start.outputs) {
        if (start.outputs[output].connections.length > 1) {
          this.editor.removeSingleConnection(
            output_id,
            input_id,
            output_class,
            input_class
          )

          return
        }
      }
    })

    this.editor.on('click', event => {
      if (this.tooltipTimeout) {
        clearTimeout(this.tooltipTimeout)
      }
      if (this.tooltip.active) {
        this.tooltip.active = false
      }

      const paths = event.path || (event.composedPath && event.composedPath())
      if (!paths) return

      if (
        paths[0] &&
        paths[0].getAttribute('class') &&
        paths[0].getAttribute('class').includes('main-path')
      ) {
        const classes = paths[1].getAttribute('class').split(' ')

        let outputId = ''
        let outputOrder = ''
        for (const item of classes) {
          if (item.includes('node_out_node-')) {
            outputId = item.replace('node_out_node-', '')
          } else if (item.includes('output_')) {
            outputOrder = item.replace('output_', '')
          }
        }

        const data = this.editor.getNodeFromId(outputId)

        if (data && data.data && data.data.node === 'condition') {
          const { property } = data.data
          const conditions = property.conditions

          this.tooltip = {
            active: true,
            text:
              outputOrder == conditions.length
                ? 'Else'
                : conditions[outputOrder - 1].condition,
            positionX: event.pageX,
            positionY: event.pageY
          }

          this.tooltipTimeout = setTimeout(() => {
            this.tooltip.active = false
          }, 2000)
        }
      }

      // node selected support editor mode => fixed & edit
      for (const div of paths) {
        if (
          div &&
          'getAttribute' in div &&
          div.getAttribute('id') &&
          div.getAttribute('id').includes('node-')
        ) {
          const id = div.getAttribute('id').replace('node-', '')

          this.isNameDuplicated = ''
          this.errorFromValidate = ''

          const data = this.editor.getNodeFromId(id)
          if (data) this.dataNodeSelected = data
          else this.dataNodeSelected = {}
        }
      }
    })

    this.editor.on('translate', ({ x, y }) => {
      const index = _.findIndex(this.tabs, { name: this.tabSelected })
      this.tabs[index].position = { x, y }
    })

    this.editor.on('clickEnd', e => {
      const shiftKey = e.shiftKey

      if (this.editor.node_selected !== null) {
        const nodeId = this.editor.node_selected.id.slice(5)
        const indexSelected = this.nodesSelected.indexOf(nodeId)
        if (indexSelected === -1) {
          if (shiftKey) this.nodesSelected.push(nodeId)
          else {
            this.nodesSelected.forEach(eleN => {
              const node = document.getElementById(`node-${eleN}`)
              if (node) node.classList.remove('selected')
            })
            this.nodesSelected.splice(0, this.nodesSelected.length)

            this.nodesSelected.push(nodeId)
          }
        } else {
          this.nodesSelected.splice(indexSelected, 1)
          const nodeUnselected = document.getElementById(`node-${nodeId}`)
          if (nodeUnselected) nodeUnselected.classList.remove('selected')
        }
      } else {
        this.nodesSelected.forEach(eleN => {
          const node = document.getElementById(`node-${eleN}`)
          if (node) node.classList.remove('selected')
        })
        this.nodesSelected.splice(0, this.nodesSelected.length)
      }
      this.nodesSelected.forEach(eleN => {
        const node = document.getElementById(`node-${eleN}`)
        if (node) node.classList.add('selected')
      })
    })

    this.editor.on('mouseMove', ({ x, y }) => {
      if (this.editor.node_selected && this.editor.drag) {
        this.editor.node_selected.classList.add('selected')
        const nodeId = this.editor.node_selected.id.slice(5)
        const indexSelected = this.nodesSelected.indexOf(nodeId)
        if (indexSelected === -1) {
          this.nodesSelected.push(nodeId)
        }
        this.nodesSelected.forEach(eleN => {
          if (eleN != this.editor.node_selected.id.slice(5)) {
            const node = document.getElementById(`node-${eleN}`)
            const xnew =
              ((this.last_x - x) * this.editor.precanvas.clientWidth) /
              (this.editor.precanvas.clientWidth * this.editor.zoom)
            const ynew =
              ((this.last_y - y) * this.editor.precanvas.clientHeight) /
              (this.editor.precanvas.clientHeight * this.editor.zoom)

            node.style.top = node.offsetTop - ynew + 'px'
            node.style.left = node.offsetLeft - xnew + 'px'

            this.editor.drawflow.drawflow[this.editor.module].data[eleN].pos_x =
              node.offsetLeft - xnew
            this.editor.drawflow.drawflow[this.editor.module].data[eleN].pos_y =
              node.offsetTop - ynew
            this.editor.updateConnectionNodes(`node-${eleN}`)
          }
        })
      }
      this.last_x = x
      this.last_y = y
    })

    this.editor.on('nodeMoved', id => {
      const data = this.editor.getNodeFromId(id)
      const pos_x = data.pos_x
      const pos_y = data.pos_y

      this.editor.drawflow.drawflow[this.tabSelected].data[id].pos_x = pos_x
      this.editor.drawflow.drawflow[this.tabSelected].data[id].pos_y = pos_y
      document.getElementById(`node-${id}`).style.left = pos_x + 'px'
      document.getElementById(`node-${id}`).style.top = pos_y + 'px'
      this.editor.updateConnectionNodes(`node-${id}`)
    })

    this.editor.removeNodeId = id => {
      const nodeClick = id.slice(5)

      let nodeShouldDelete = []
      if (this.nodesSelected.includes(nodeClick)) {
        nodeShouldDelete = _.cloneDeep(this.nodesSelected)
      } else {
        nodeShouldDelete.push(nodeClick)
      }

      for (const node of nodeShouldDelete) {
        const data = this.editor.getNodeFromId(node)

        if (data.class !== 'durable') {
          this.editor.removeConnectionNodeId(`node-${node}`)
          var moduleName = this.editor.getModuleFromNodeId(node)
          if (this.editor.module === moduleName) {
            document.getElementById(`node-${node}`).remove()
          }
          delete this.editor.drawflow.drawflow[moduleName].data[node]
          this.editor.dispatch('nodeRemoved', node)
        }
      }
    }

    bus.$on('nodeData', id => {
      if (!id || !this.editor) return

      const node = this.editor.getNodeFromId(id.slice(5))
      if (node) {
        bus.$emit('SendNodeData', { id, data: node.data })
      }
    })
    bus.$on('openDrawflowDetail', () => {
      this.toggleShowDetail(true)
    })
    bus.$on('closeDrawflowDetail', () => {
      this.isNameDuplicated = ''
      this.toggleShowDetail(false)
    })
    bus.$on('confirmDrawflowDetail', callback => {
      const { node, data } = callback

      this.editor.updateNodeDataFromId(node.id, data)

      this.dataNodeSelected = this.editor.getNodeFromId(node.id)

      // update name
      const tab = this.tabSelected

      let draw = this.editor.export()
      if (!draw.drawflow[tab]) return

      let drawNode = draw.drawflow[tab].data[node.id]

      if (!drawNode) return

      // check name was dupicated or not
      this.isNameDuplicated = ''
      this.errorFromValidate = ''

      if (this.$nodeNameUnique.includes(drawNode.data.node)) {
        for (const tab in draw.drawflow) {
          for (const component in draw.drawflow[tab].data) {
            if (
              drawNode.data.name.toUpperCase() ===
                draw.drawflow[tab].data[component].name.toUpperCase() &&
              drawNode.id !== draw.drawflow[tab].data[component].id
            ) {
              this.isNameDuplicated = drawNode.data.name
              return
            }
          }
        }
      }
      drawNode.name = drawNode.data.name

      let page = {}
      if (drawNode.data.node === 'page') {
        if (drawNode.data.property.type !== 'page_name') {
          page = _.find(this.tabs, {
            id: drawNode.data.property.page
          })
        }

        if (page.type === 'terminal') {
          drawNode.data.ui.icon = 'fa-file'
          drawNode.class = 'nooutput'
          drawNode.html = 'nooutput'
        } else {
          drawNode.data.ui.icon = 'far fa-file'
          drawNode.class = 'normal'
          drawNode.html = 'normal'
        }
      }

      // check jump
      if (drawNode.data.node === 'jump') {
        if (drawNode.data.property.type === 'component') {
          drawNode.data.ui.icon = 'fa-location-arrow'
        } else {
          drawNode.data.ui.icon = 'fa-right-from-bracket'
        }
      }

      this.editor.import(draw)

      // check page output
      if (drawNode.data.node === 'page') {
        draw = this.editor.export()
        drawNode = draw.drawflow[tab].data[node.id]

        if (
          page.type === 'terminal' &&
          drawNode.outputs.output_1 &&
          drawNode.outputs.output_1.connections
        ) {
          // if have output path
          if (drawNode.outputs.output_1.connections[0])
            this.editor.removeSingleConnection(
              drawNode.id,
              drawNode.outputs.output_1.connections[0].node,
              'output_1',
              'input_1'
            )

          this.editor.removeNodeOutput(drawNode.id, 'output_1')
        } else if (page.type === 'normal' && !drawNode.outputs.output_1) {
          this.editor.addNodeOutput(drawNode.id)
        }
      }

      // check condition && random output
      if (['condition', 'random'].includes(drawNode.data.node)) {
        draw = this.editor.export()
        drawNode = draw.drawflow[tab].data[node.id]

        let cond = ''
        let nodeClass = ''

        switch (drawNode.data.node) {
          case 'condition': {
            cond = drawNode.data.property.conditions
            nodeClass = 'condition'
            break
          }
          case 'random': {
            cond = drawNode.data.property.routes
            nodeClass = 'random'
            break
          }
        }
        const output = drawNode.outputs

        const condLength = cond.length
        const outputLength = Object.keys(output).length

        drawNode.class = `${nodeClass}-${condLength}`
        drawNode.html = `${nodeClass}-${condLength}`
        this.editor.import(draw)

        const nodeAdded = condLength - outputLength
        // add outputs
        if (nodeAdded > 0) {
          for (let i = 0; i < nodeAdded; i++) {
            this.editor.addNodeOutput(drawNode.id)
          }
        }
        // delete outputs
        else if (nodeAdded < 0) {
          for (let i = 0; i < Math.abs(nodeAdded); i++) {
            // if have output path
            if (
              drawNode.outputs[`output_${i + 1}`] &&
              drawNode.outputs[`output_${i + 1}`].connections[0]
            )
              this.editor.removeSingleConnection(
                drawNode.id,
                drawNode.outputs[`output_${i + 1}`].connections[0].node,
                `output_${i + 1}`,
                'input_1'
              )
            this.editor.removeNodeOutput(drawNode.id, `output_${i + 1}`)
          }
        }
      }

      this.toggleShowDetail(false)
      this.getAllComponentItemsInTabs()
    })
    // bus.$on('saveImport', data => {
    //   this.importFlow(data)
    // })

    this.initialFlowUi()
    this.getTtsChoice()
    //------use for Intellisense------
    this.getApiForIntellisense()
    //------use for Intellisense------
  },
  methods: {
    setCanvas(x, y) {
      const canvas_x = x
      const canvas_y = y
      this.editor.precanvas.style.transform = `translate(${canvas_x}px, ${canvas_y}px) scale(1)`
      this.editor.canvas_x = x
      this.editor.canvas_y = y
    },
    getCoords(element) {
      const clientRect = element.getBoundingClientRect()
      return {
        left: clientRect.left + document.body.scrollLeft,
        top: clientRect.top + document.body.scrollTop
      }
    },
    checkMove(evt) {
      return (
        evt.draggedContext.element.name !== 'Home' &&
        evt.draggedContext.futureIndex !== 0
      )
    },
    editorStart() {
      this.editor = new Drawflow(document.getElementById('drawflow'), Vue)
      this.editor.start()
      this.editor.createCurvature = function(
        start_x,
        start_y,
        end_x,
        end_y,
        curvature_value,
        type
      ) {
        const center_y = (end_y - start_y) / 2 + start_y
        const default_round_radius = 5
        const round_radius = Math.min(
          default_round_radius,
          Math.abs(start_x - end_x),
          Math.abs(start_y - end_y)
        )

        const isRight = end_x > start_x
        const isDown = end_y > start_y
        return `M ${start_x} ${start_y}
            L ${start_x} ${
          isDown ? center_y - round_radius : center_y + round_radius
        }
            A ${round_radius} ${round_radius} 0 0 ${
          isRight ^ !isDown ? 0 : 1
        } ${
          isRight ? start_x + round_radius : start_x - round_radius
        } ${center_y}
            L ${
              isRight ? end_x - round_radius : end_x + round_radius
            } ${center_y}
            A ${round_radius} ${round_radius} 0 0 ${
          isRight ^ !isDown ? 1 : 0
        } ${end_x} ${isDown ? center_y + round_radius : center_y - round_radius}
            L ${end_x} ${end_y}`
      }
    },
    async initialFlowUi(isClear) {
      // all module
      for (const tab of this.tabs) {
        this.editor.addModule(tab.name)
      }
      this.pageClick(this.tabs[0].name)

      const props = {}
      const options = {}

      this.editor.registerNode('normal', ComponentNormal, props, options)
      this.editor.registerNode('durable', ComponentNormal, props, options)

      this.editor.registerNode('mapping', ComponentNormal, props, options)
      this.editor.registerNode('mapping-input', ComponentNormal, props, options)
      this.editor.registerNode('mapping-api', ComponentNormal, props, options)
      this.editor.registerNode('mapping-mail', ComponentNormal, props, options)
      this.editor.registerNode('schedule', ComponentNormal, props, options)

      this.editor.registerNode('nooutput', ComponentNormal, props, options)
      this.editor.registerNode('comment', ComponentNormal, props, options)

      // condition && random component
      for (let i = 1; i <= 10; i++) {
        this.editor.registerNode(
          `condition-${i}`,
          ComponentNormal,
          props,
          options
        )

        this.editor.registerNode(`random-${i}`, ComponentNormal, props, options)
      }

      // first node
      // get flow from db
      if (!isClear) {
        this.loadingComplete = false

        const flowUi = await apiFlow.getFlowUi(this.flowId)
        this.flowName = flowUi.data.name
        this.flowPlatform = flowUi.data.platform
        this.flowVersion = flowUi.data.__v

        if (flowUi && flowUi.data && flowUi.data.flow_ui) {
          const json = JSON.parse(flowUi.data.flow_ui)
          this.tabs = json.tabs

          const arrange = new Arrange()
          const { tabs, ui } = await arrange.checkUiAvailable({
            tabs: json.tabs,
            ui: json.ui,
            platform: flowUi.data.platform
          })
          this.tabs = tabs
          this.structure = this.collapseAllFolder(json.structure)

          this.editor.import(ui)

          // this.pageClick(this.tabs[0].name)
          this.getAllComponentItemsInTabs()
          // init Home flow history
          this.flowHistory[this.tabs[0].name][0] = json.ui

          // set position when start home
          const { x, y } = this.tabs[0].position
            ? this.tabs[0].position
            : { x: 0, y: 0 }
          this.setCanvas(x, y)

          this.setPageLock(this.tabs[0].locked || false)
          this.isEnableCall = true
        } else {
          // first node
          this.addStartComponent()
          this.isEnableCall = false
        }

        if (this.flowPlatform === 'chat') {
          this.componentItems = _.cloneDeep(this.componentChatItems)
        } else {
          this.componentItems = _.cloneDeep(this.componentVoiceItems)
        }

        // temp flow
        this.saveFlowToTemp()

        this.loadingComplete = true
      } else {
        this.addStartComponent()
      }
    },
    collapseAllFolder(data) {
      if (data) {
        let i = data.length
        while (i--) {
          // page
          if (data[i].isLeaf) {
            const index = _.findIndex(this.tabs, { name: data[i].title })

            // fixed bugs : CHAT-2818
            if (index > -1) {
              if (data[i].data) {
                data[i].data.id = this.tabs[index].id
              }
            }
            // fixed bugs : https://github.com/Truevoice/kpat-core-frontend/tree/ticket/CHAT-2811
            else {
              data.splice(i, 1)
            }
          }
          // folder
          else {
            data[i].isExpanded = false

            if (data[i].children) {
              data[i].children = this.collapseAllFolder(data[i].children)
            }
          }
        }
        // for (let i = 0; i < data.length; i++) {
        //   // fixed bugs : CHAT-2818
        //   const index = _.findIndex(this.tabs, { name: data[i].title })
        //   if (data[i].data && index > -1) {
        //     data[i].data.id = this.tabs[index].id
        //   }

        //   if (!data[i].isLeaf) {
        //     data[i].isExpanded = false
        //   }
        //   if (data[i] && data[i].children) {
        //     data[i].children = this.collapseAllFolder(data[i].children)
        //   }
        // }
      }

      return data
    },
    //====== add for intentgent =====
    async getApiForIntellisense() {
      try {
        let flowBuilderVariables = []
        const getTestNlu = await apiNlu.getNluEntity()
        //======== add field ทั้งหมดเข้า array =====
        const getField = await apiField.activeField()
        const { data } = getField
        for (const obj of data) {
          flowBuilderVariables.push(
            '{system.fields.' + `${obj.group}.${obj.name}` + '}'
          )
        }
        //======== add field ทั้งหมดเข้า array =====
        this.apiForIntellisense = {
          flowBuilderVariables: flowBuilderVariables,
          nluEntity: getTestNlu.data
        }
      } catch (error) {}
    },
    //====== add for intentgent =====

    async getTtsChoice() {
      try {
        const getTtsChoice = await apiSynthesis.getTtsChoice()
        const { data } = getTtsChoice
        for (let i = 0; i < data.length; i++) {
          const obj = {}
          obj._id = data[i]._id ? data[i]._id : ''
          obj.voice = data[i].voice ? data[i].voice : ''
          obj.name = data[i].name ? data[i].name : ''
          obj.description = data[i].description ? data[i].description : ''
          obj.engine = data[i].engine ? data[i].engine.toLowerCase() : ''
          obj.speaker = data[i].speaker ? data[i].speaker : ''
          obj.isDefault = data[i].isDefault ? data[i].isDefault : false
          obj.sample_utterance = data[i].sample_utterance || ''
          obj.icon =
            obj.engine === 'botnoi'
              ? BotnoiIcon
              : obj.engine === 'google'
              ? GoogleIcon
              : obj.engine === 'readspeaker'
              ? ReadSpeakerIcon
              : obj.engine === 'azure' || obj.engine === 'microsoft'
              ? AzureIcon
              : obj.engine === 'botnoiv3'
              ? BotnoiIcon
              : ''
          this.ttsChoices.push(obj)
        }
      } catch (error) {}
    },

    // start global action
    pushFlowHistory() {
      const tab = this.tabSelected
      let history = Object.assign([], this.flowHistory[tab])
      let index = this.flowIndex[tab]
      const maxLength = this.flowHistoryLimit

      history = history.slice(0, index + 1)

      history.push(JSON.parse(JSON.stringify(this.editor.export())))
      index += 1

      if (history.length > maxLength) {
        history.shift()
        index = maxLength - 1
      }

      this.flowHistory[tab] = history
      this.flowIndex[tab] = index

      this.toggleUndoRedo()
    },
    undo() {
      const tab = this.tabSelected
      const history = Object.assign([], this.flowHistory[tab])
      let index = this.flowIndex[tab]

      if (index > 0) {
        index -= 1
        const data = history[index]

        const all = JSON.parse(JSON.stringify(this.editor.export()))
        all.drawflow[tab] = data.drawflow[tab]
        this.editor.import(all)

        this.flowIndex[tab] = index

        this.toggleUndoRedo()
      }
    },
    redo() {
      const tab = this.tabSelected
      const history = Object.assign([], this.flowHistory[tab])
      let index = this.flowIndex[tab]

      if (index < history.length - 1) {
        index += 1

        const data = history[index]

        const all = JSON.parse(JSON.stringify(this.editor.export()))
        all.drawflow[tab] = data.drawflow[tab]
        this.editor.import(all)

        this.flowIndex[tab] = index

        this.toggleUndoRedo()
      }
    },
    pushPageHistory() {
      if (this.pageHistory.at(-1) === this.tabSelected) return

      this.pageHistory = this.pageHistory.slice(0, this.pageIndex + 1)
      this.pageHistory.push(this.tabSelected)
      this.pageIndex += 1

      this.toggleBackwardForward()
    },
    clearPageHistory() {
      this.pageHistory = []
      this.pageHistory.push(this.tabs[0].name)
      this.pageIndex = 0
    },
    backward() {
      if (this.pageIndex > 0) {
        this.pageIndex -= 1

        const tab = this.pageHistory[this.pageIndex]
        this.pageClick(tab, { noPushPage: false })

        // scroll to page
        const tabIndex = _.findIndex(this.tabs, { name: tab })
        // this.scrollXParentToChild(tabIndex)

        this.toggleBackwardForward()
      }
    },
    forward() {
      if (this.pageIndex < this.pageHistory.length - 1) {
        this.pageIndex += 1

        const tab = this.pageHistory[this.pageIndex]
        this.pageClick(tab, { noPushPage: false })

        // scroll to page
        const tabIndex = _.findIndex(this.tabs, { name: tab })
        // this.scrollXParentToChild(tabIndex)

        this.toggleBackwardForward()
      }
    },
    async save(overwrite) {
      this.btnSaveLoading = true

      const valid = this.validate()

      if (!valid.is_valid) {
        // auto selection
        this.pageClick(valid.page)

        setTimeout(() => {
          this.nodesSelected = []
          const node = document.getElementById(`node-${valid.id}`)
          if (node) {
            // set node to center
            const args = {
              node_x: this.editor.drawflow.drawflow[valid.page].data[valid.id]
                .pos_x,
              node_y: this.editor.drawflow.drawflow[valid.page].data[valid.id]
                .pos_y,
              node_w: node.clientWidth,
              node_h: node.clientHeight,
              canvas_w: this.editor.precanvas.clientWidth,
              canvas_h: this.editor.precanvas.clientHeight
            }
            const pos_x = -args.node_x + args.canvas_w / 2 - args.node_w / 2
            const pos_y = -args.node_y + args.canvas_h / 2 - args.node_h / 2
            this.setCanvas(pos_x, pos_y)

            if (valid.open_panel) {
              const data = this.editor.getNodeFromId(valid.id)
              if (data) this.dataNodeSelected = data
              else this.dataNodeSelected = {}

              setTimeout(() => {
                this.errorFromValidate = valid.message
                this.toggleShowDetail(true)
              }, 300)
            } else {
              this.snackbar.colorSnackbar = 'error'
              this.snackbar.textSnackbar = valid.message
              this.snackbar.open = true

              node.classList.add('selected')
              this.nodesSelected.push(valid.id)
            }

            this.btnSaveLoading = false
          }
        }, 100)

        return
      }

      const obj = {
        tabs: this.tabs,
        structure: this.structure,
        ui: this.editor.export()
      }

      const transform = new Transform(_.cloneDeep(obj))
      const json = transform.transformComponent()

      try {
        const flow = await apiFlow.saveFlowUi(this.flowId, {
          flow_ui: obj,
          flow_bot: json,
          __v: this.flowVersion,
          overwrite
        })

        if (flow.code === 0) {
          this.flowVersion = flow.data.__v

          this.snackbar.colorSnackbar = 'success'
          this.snackbar.textSnackbar = `Saved`
          this.snackbar.open = true
          if (!this.isEnableCall) this.isEnableCall = true
        } else {
          if (flow.message === 'Overwrite') {
            this.dialogConfirm.action = 'overwrite'
            this.dialogConfirm.open = true
            this.dialogConfirm.name = this.flowId
            this.dialogConfirm.message = `<b>${
              flow.data.email
            }</b> recently made changes to this flow on <b>${this.$options.filters.datetime(
              flow.data.updated_at
            )}</b><br/><br/>Overwrite their changes?`
          }
        }

        this.saveFlowToTemp()
      } catch (err) {
        this.snackbar.colorSnackbar = 'error'
        this.snackbar.textSnackbar = `Couldn't save to DB`
        this.snackbar.open = true
      } finally {
        this.btnSaveLoading = false
      }
    },
    validate() {
      const draw = this.editor.export()

      const transform = new Transform({}, this.tabs)
      const valid = transform.validateBeforeSave(draw)

      return valid
    },
    validatePage(pageName) {
      const data = this.getDrawFlowPage(pageName).data

      for (const [key, value] of Object.entries(data)) {
        // check input
        if (Object.keys(value.inputs).length > 0) {
          for (const input in value.inputs) {
            if (value.inputs[input].connections.length === 0)
              return {
                is_valid: false,
                message: `${pageName} : component ${value.name} - input is required`
              }
          }
        }

        // check output
        if (Object.keys(value.outputs).length > 0) {
          for (const output in value.outputs) {
            if (value.outputs[output].connections.length === 0)
              return {
                is_valid: false,
                message: `${pageName} : component ${value.name} - output is required`
              }
          }
        }
      }

      return { is_valid: true }
    },
    toggleUndoRedo() {
      const tab = this.tabSelected
      const index = this.flowIndex[tab]
      const history = Object.assign([], this.flowHistory[tab])

      // undo
      if (index === 0) this.isEnableUndo = false
      else this.isEnableUndo = true

      // redo
      if (index !== history.length - 1 && history.length > 1)
        this.isEnableRedo = true
      else this.isEnableRedo = false
    },
    toggleBackwardForward() {
      // backward
      if (this.pageIndex <= 0) this.isEnableBackward = false
      else this.isEnableBackward = true

      // forward
      if (
        this.pageIndex !== this.pageHistory.length - 1 &&
        this.pageHistory.length > 1
      )
        this.isEnableForward = true
      else this.isEnableForward = false
    },
    closeVersionHistory() {
      this.dialogVersionHistoryIsOpen = false
    },
    async optionClick(action) {
      switch (action) {
        case 'versionHistory':
          this.dialogVersionHistoryIsOpen = true

          break
        case 'import':
          this.dialogImportIsOpen = true

          break
        case 'export':
          this.exportFlow()

          break
        case 'clearAll':
          this.confirmClearAll()

          break
        default:
          break
      }
    },
    pageOptionClick(action) {
      // this.tabCreate(action, this.tabs.length)
      //  <!-- ticket/CHAT-2211-2212-2213 flow templates-->
      if (action == 'template') {
        this.dialogTemplateIsOpen = true
      }
      //  <!-- ticket/CHAT-2211-2212-2213 flow templates-->
      setTimeout(() => {
        const container = document.getElementById('ul-tab')
        container.scrollLeft = container.clientWidth
      }, 100)
    },
    // end global action

    // start tab action
    pageClick(tabName, option) {
      if (!tabName) return

      this.editor.changeModule(tabName)
      this.tabSelected = tabName

      // start flow history
      if (typeof this.flowIndex[this.tabSelected] === 'undefined') {
        this.flowIndex[this.tabSelected] = 0
        this.flowHistory[this.tabSelected] = [
          { ...JSON.parse(JSON.stringify(this.editor.export())) }
        ]
      }

      this.toggleUndoRedo()
      if (!option || option.noPushPage) this.pushPageHistory()
      this.getAllComponentItemsInTabs()

      const index = _.findIndex(this.tabs, { name: this.tabSelected })
      if (index === -1) return

      const { x, y } = this.tabs[index].position
        ? this.tabs[index].position
        : { x: 0, y: 0 }
      this.setCanvas(x, y)

      // clear node selected
      this.nodesSelected = []
      this.last_x = 0
      this.last_y = 0
      this.dataNodeSelected = {}

      this.setPageLock(this.tabs[index].locked || false)
    },
    componentClick({ page, componentId }) {
      // auto selection
      this.pageClick(page)

      setTimeout(() => {
        this.nodesSelected = []
        const node = document.getElementById(`node-${componentId}`)
        if (node) {
          // set node to center
          const args = {
            node_x: this.editor.drawflow.drawflow[page].data[componentId].pos_x,
            node_y: this.editor.drawflow.drawflow[page].data[componentId].pos_y,
            node_w: node.clientWidth,
            node_h: node.clientHeight,
            canvas_w: this.editor.precanvas.clientWidth,
            canvas_h: this.editor.precanvas.clientHeight
          }
          const pos_x = -args.node_x + args.canvas_w / 2 - args.node_w / 2
          const pos_y = -args.node_y + args.canvas_h / 2 - args.node_h / 2
          this.setCanvas(pos_x, pos_y)

          const data = this.editor.getNodeFromId(componentId)
          if (data) this.dataNodeSelected = data
          else this.dataNodeSelected = {}

          setTimeout(() => {
            this.toggleShowDetail(true)
          }, 300)
        }
      }, 100)
    },

    pageCreate({ nodes, replaceId, name }) {
      this.editor.addModule(name)

      this.pageClick(name)
      const pageAccess = this.addPageComponent('normal', name)

      this.tabs.push(pageAccess)

      let str = JSON.stringify(nodes)
      str = str.replace(`"id":"${replaceId}"`, `"id":${pageAccess.id}`)
      this.structure = JSON.parse(str)
    },
    pageCloneFromTemplate(data) {
      this.dialogTemplate.open = true
      this.dialogTemplate.data = data
    },
    pageConfirmRemove({ nodes, tab }) {
      this.dialogConfirm.action = 'removeTab'
      this.dialogConfirm.open = true
      this.dialogConfirm.name = tab.name
      this.dialogConfirm.message = `Delete <b>${tab.name}</b> ?`
      this.dialogConfirm.nodes = nodes
    },
    pageRename({ pageId, oldName, newName, nodes }) {
      const data = this.editor.export()
      this.editor.changeModule('Home')

      delete Object.assign(data.drawflow, {
        [newName]: data.drawflow[oldName]
      })[oldName]

      const index = _.findIndex(this.tabs, { id: pageId })
      this.tabs[index].name = newName

      this.editor.import(data)
      this.editor.changeModule(newName)

      this.tabSelected = newName

      this.structure = nodes

      // rename in page history
      this.pageHistory = _.map(this.pageHistory, el => {
        return el === oldName ? newName : el
      })

      // rename in page copy
      this.copyNodes.page =
        this.copyNodes.page === oldName ? newName : this.copyNodes.page
    },
    confirmRestore(data) {
      this.dialogVersionHistoryIsOpen = false

      this.dialogConfirm.action = 'restore'
      this.dialogConfirm.open = true
      this.dialogConfirm.name = data._id
      this.dialogConfirm.message = `Restore to version <b>${
        data.description ? data.description : ''
      }</b> created by ${data.email} on <b>${this.$options.filters.datetime(
        data.created_at
      )}</b> ?`
    },
    confirmClearAll() {
      this.dialogConfirm.action = 'clearAll'
      this.dialogConfirm.open = true
      this.dialogConfirm.name = ''
      this.dialogConfirm.message = `Clear all pages ?`
    },
    async confirmAction(action) {
      if (action === 'restore') {
        try {
          // CHAT-2819
          const flowUi = await apiFlowLog.getFlowLog(this.dialogConfirm.name)

          this.editor.clear()
          this.pageClick('Home', { noPushPage: false })

          const json = JSON.parse(flowUi.data.flow_ui)

          this.tabs = json.tabs
          this.structure = json.structure
          this.editor.import(json.ui)

          this.snackbar.colorSnackbar = 'success'
          this.snackbar.textSnackbar = 'Flow restored'
          this.snackbar.open = true
        } catch {}
      } else if (action === 'removeTab') {
        const tab = this.dialogConfirm.name
        const index = _.findIndex(this.tabs, { name: tab })
        if (index !== -1) {
          this.tabs.splice(index, 1)
          this.structure = this.dialogConfirm.nodes

          this.editor.removeModule(tab)
          if (this.tabSelected === tab) this.tabSelected = 'Home'
        }

        // remove in page history
        const tempLength = this.pageHistory.length
        const afterPageRemoved = _.filter(this.pageHistory, el => {
          return el !== tab
        })

        // check duplicate of page history
        this.pageHistory = []
        for (const el of afterPageRemoved) {
          if (this.pageHistory.at(-1) !== el) this.pageHistory.push(el)
        }

        this.pageIndex = this.pageIndex - tempLength + this.pageHistory.length
        if (this.pageIndex < -1) this.pageIndex - 1

        this.toggleBackwardForward()
      } else if (action === 'clearAll') {
        this.editor.clear()

        this.tabs = [{ id: 1, name: 'Home' }]
        this.initialFlowUi(true)
      } else if (action === 'overwrite') {
        this.save(true)
      } else if (action === 'removeTemplate') {
        const { id, name } = this.dialogConfirm
        this.deleteTemplate(id, name)
      }

      this.dialogConfirm = {
        action: '',
        open: false,
        name: '',
        message: '',
        nodes: []
      }
    },
    // end tab action

    toggleShowDetail(isShow) {
      this.isShowDetail = isShow
    },

    allowDrop(ev) {
      ev.preventDefault()
    },
    drag(ev) {
      if (ev.type === 'touchstart') {
        const data = ev.target.closest('.drag-drawflow')
        this.mobileItemSelect = data.getAttribute('data-node')
      } else {
        ev.dataTransfer.setData('data', ev.target.getAttribute('data-node'))
      }
    },
    drop(ev) {
      if (ev.type === 'touchend') {
        const parentdrawflow = document
          .elementFromPoint(
            this.mobileLastMove.touches[0].clientX,
            this.mobileLastMove.touches[0].clientY
          )
          .closest('#drawflow')
        if (parentdrawflow != null) {
          this.addNodeToDrawFlow(
            this.mobileItemSelect,
            this.mobileLastMove.touches[0].clientX,
            this.mobileLastMove.touches[0].clientY
          )
        }
        this.mobileItemSelect = {}
      } else {
        ev.preventDefault()
        const data = ev.dataTransfer.getData('data')
        this.addNodeToDrawFlow(data, ev.clientX, ev.clientY)
      }
    },
    addNodeToDrawFlow(obj, pos_x, pos_y) {
      if (this.isLocked) return

      const data = JSON.parse(obj)

      let x =
        pos_x *
          (this.editor.precanvas.clientWidth /
            (this.editor.precanvas.clientWidth * this.editor.zoom)) -
        this.editor.precanvas.getBoundingClientRect().x *
          (this.editor.precanvas.clientWidth /
            (this.editor.precanvas.clientWidth * this.editor.zoom))
      let y =
        pos_y *
          (this.editor.precanvas.clientHeight /
            (this.editor.precanvas.clientHeight * this.editor.zoom)) -
        this.editor.precanvas.getBoundingClientRect().y *
          (this.editor.precanvas.clientHeight /
            (this.editor.precanvas.clientHeight * this.editor.zoom))

      const dataMapping = new DataMapping(this.editor, this.$nodeNameUnique)
      const mapping = dataMapping.getDataMapping(data)

      const input = data.type === 'comment' ? 0 : 1
      let output = 1
      switch (data.type) {
        case 'condition-2':
        case 'random-2':
        case 'mapping':
        case 'mapping-input':
        case 'mapping-api':
        case 'mapping-mail':
        case 'schedule':
          output = 2
          break
        case 'nooutput':
        case 'comment':
          output = 0
          break
      }

      this.editor.addNode(
        mapping.name,
        input,
        output,
        x,
        y,
        data.type,
        mapping,
        data.type,
        'vue'
      )
    },
    getAllComponentItemsInTabs() {
      const tab = this.tabSelected
      const drawflow = JSON.parse(JSON.stringify(this.editor.export()))
      const data = drawflow.drawflow[tab].data
      this.drawFlowItems = drawflow.drawflow

      this.items = []
      for (const index in data) {
        this.items.push({
          id: data[index].id,
          name: data[index].data.name,
          type: data[index].data.node,
          icon: data[index].data.ui ? data[index].data.ui.icon : ''
        })
      }

      this.adjustOutputLabel(data)
      this.drawflowUi = this.editor.export()
    },
    addStartComponent() {
      const dataMapping = new DataMapping(this.editor, this.$nodeNameUnique)
      const data = {
        icon: 'fa-home',
        title: 'Start',
        name: this.flowPlatform === 'chat' ? 'chat_start' : 'start',
        type: 'durable'
      }
      this.editor.addNode(
        data.name,
        0,
        1,
        500,
        10,
        data.type,
        dataMapping.getDataMapping(data),
        data.type,
        'vue'
      )
    },
    addPageComponent(type, tab) {
      const dataMapping = new DataMapping(this.editor, this.$nodeNameUnique)
      const enter = {
        icon: 'fa-sign-in-alt',
        title: 'Enter',
        name: 'enter',
        type: 'durable'
      }
      this.editor.addNode(
        enter.name,
        0,
        1,
        500,
        10,
        enter.type,
        dataMapping.getDataMapping(enter),
        enter.type,
        'vue'
      )

      if (type === 'normal') {
        const dataMapping = new DataMapping(this.editor, this.$nodeNameUnique)
        const jump = {
          icon: 'fa-right-from-bracket',
          title: 'Exit',
          name: 'jump',
          type: 'nooutput',
          start: 'exit_page'
        }
        this.editor.addNode(
          jump.name,
          1,
          0,
          500,
          300,
          jump.type,
          dataMapping.getDataMapping(jump),
          jump.type,
          'vue'
        )
      }

      const editor = this.editor.export()

      const [first] = Object.keys(editor.drawflow[tab].data)
      return { id: parseInt(first), name: tab, type }
    },
    goToPage(pageId) {
      const tabIndex = _.findIndex(this.tabs, { id: pageId })
      if (tabIndex > -1) {
        const tab = this.tabs[tabIndex]
        this.pageClick(tab.name)

        // this.scrollXParentToChild(tabIndex)
      }

      this.dialogPage = false
    },
    // scrollXParentToChild(tabIndex) {
    //   setTimeout(() => {
    //     const parent = document.getElementById('ul-tab')
    //     const child = document.getElementById(`tab${tabIndex}`)

    //     const parentRect = parent.getBoundingClientRect()
    //     const parentViewableArea = {
    //       height: parent.clientHeight,
    //       width: parent.clientWidth
    //     }
    //     const childRect = child.getBoundingClientRect()
    //     const isViewable =
    //       childRect.left >= parentRect.left &&
    //       childRect.left <= parentRect.left + parentViewableArea.width

    //     if (!isViewable) {
    //       parent.scrollLeft =
    //         childRect.left +
    //         parent.scrollLeft -
    //         parentRect.left -
    //         childRect.width
    //     }
    //   }, 100)
    // },
    openNewFlowBuilder(item) {
      // this.$router.push({
      //   path: `/flows/builder/${item._id}`
      // })
      const routeData = this.$router.resolve({
        path: `/flows/builder/${item._id}`
      })
      window.open(routeData.href, '_blank')
    },
    pageKeyDown(event) {
      switch (event.keyCode) {
        case 27: // esc
          this.dialogPage = false
          break
      }
    },
    searchTabsMenuWord() {
      if (this.tabsMenuWord) {
        const filter = _.filter(this.tabsSort, o => {
          return o.name.toLowerCase().includes(this.tabsMenuWord.toLowerCase())
        })

        this.tabsSortAndFilter = filter
      } else {
        this.tabsSortAndFilter = _.cloneDeep(this.tabsSort)
      }
    },
    clearTabsMenuWord() {
      this.tabsMenuWord = ''
    },
    adjustOutputLabel(data) {
      for (const [id, ele] of Object.entries(data)) {
        if (ele && ele.data && ele.data.node === 'random') {
          const { routes } = ele.data.property
          for (let i = 0; i < routes.length; i++) {
            const divId = document.querySelector(
              `#node-${id} .outputs .output:nth-child(${i + 1})`
            )
            if (divId) {
              divId.setAttribute('data-before', `${routes[i].probability}%`)
            }
          }
        }
      }
    },

    // start flow template
    optionClickTabNameOption(action, tab) {
      switch (action) {
        default:
          break
      }
    },
    getDrawFlowPage(pageName) {
      const draw = this.editor.export()
      const ui = draw.drawflow[pageName]
      return ui
    },
    setDrawFlowPage(origin, obj) {
      const { flow_template_id, name, data } = obj
      const template = JSON.parse(data)

      const arrange = new Arrange()
      let newTemplate = arrange.rearrangeComponent(origin, template)
      newTemplate = arrange.renameComponent(origin, newTemplate)
      origin.drawflow[name] = { data: newTemplate }
      this.editor.import(origin)
      return newTemplate
    },
    async addTemplate(data) {
      this.dialogTemplate = {
        open: false,
        data: {}
      }

      const { nodes, replaceId, idSelected } = data

      const template = await apiFlowTemplate.getFlowTemplateById(idSelected)
      const { flow_template_id, name, type, json_content } = template.data

      const tabName = this.checkDuplicateTabName(name)

      const newData = { drawflow: {} }
      newData.drawflow[tabName] = { data: JSON.parse(json_content) }
      const arrange = new Arrange()
      const { newUi: reorder } = arrange.reorderNodeIdAndTab({
        tabs: this.tabs,
        ui: newData
      })

      const jsonData = {
        flow_template_id,
        name: tabName,
        type,
        data: JSON.stringify(reorder.drawflow[tabName].data)
      }
      const draw = JSON.parse(JSON.stringify(this.editor.export()))

      const tabId = this.addTabFromTemplate(jsonData, nodes, replaceId)
      const templateRearranged = this.setDrawFlowPage(draw, jsonData)
      const newId = this.changeTabId(tabId, templateRearranged)
      this.pageClick(jsonData.name)

      let str = JSON.stringify(nodes)
      str = str.replace(`"id":"${replaceId}"`, `"id":${newId}`)
      str = str.replace(`"title":"${replaceId}"`, `"title":"${jsonData.name}"`)

      this.structure = JSON.parse(str)

      // scroll to page
      // const tabIndex = _.findIndex(this.tabs, { jsonData: data.name })
      // this.scrollXParentToChild(tabIndex)
    },
    addTabFromTemplate(obj, nodes, replaceId) {
      const { flow_template_id, name, type } = obj
      this.editor.addModule(name)
      const tabId = parseInt(this.tabs[this.tabs.length - 1].id) + 1

      this.tabs.push({
        id: tabId,
        name,
        type: 'normal'
      })

      return tabId
    },
    changeTabId(tabId, template) {
      const newTabId = Object.keys(template)[0]
      let newId = -1
      this.tabs.forEach(tab => {
        if (tab.id === tabId) {
          tab.id = parseInt(newTabId)
          newId = parseInt(newTabId)
        }
      })

      return newId
    },
    checkDuplicateTabName(name) {
      let tabName = name
      if (this.getTabByName(tabName)) {
        let count = 1
        do {
          count++
          tabName = `${name} ${count}`
        } while (this.getTabByName(tabName))
      }
      return tabName
    },
    getTabByName(name) {
      return _.find(this.tabs, { name })
    },
    pageSaveAsTemplate(tab) {
      const { id, name, type } = tab

      const valid = this.validatePage(name)
      if (!valid.is_valid) {
        this.snackbar.colorSnackbar = 'error'
        this.snackbar.textSnackbar = valid.message
        this.snackbar.open = true
        return false
      }

      const tabName = name.trim()
      this.editedItemTemplate = {
        id,
        name: tabName,
        oldName: tabName
      }
      this.dialogTemplateSaveIsOpen = true
    },
    onClickSaveTemplate() {
      const { id, name, oldName, flow_template_id } = this.editedItemTemplate

      const indexDuplicated = _.findIndex(this.tabs, { name })
      if (!name) {
        this.errorMessage = 'Template name is required'
        return false
      } else if (indexDuplicated > -1) {
        if (this.tabs[indexDuplicated].flow_template_id !== flow_template_id) {
          this.errorMessage = `${name} already exists`
          return false
        }
      }

      this.saveTemplate()
    },
    async saveTemplate() {
      try {
        const {
          id,
          name,
          oldName,
          flow_template_id,
          flow_template_type
        } = this.editedItemTemplate

        const arrange = new Arrange()
        const template = this.getDrawFlowPage(oldName).data
        const json_content = JSON.stringify(
          arrange.rearrangeNodeId(
            template,
            arrange.getGap(
              0,
              arrange.getMinMax(arrange.getNodeId(template), 'min')
            )
          )
        )
        const obj = {
          name,
          type: 'my template',
          platform: this.flowPlatform,
          json_content
        }
        const flowTemplate = await apiFlowTemplate.createFlowTemplate(obj)

        this.snackbar.textSnackbar = `${name} saved as template`
        this.snackbar.colorSnackbar = 'success'
        this.snackbar.open = true

        this.closeDialogSaveTemplate()
      } catch (error) {
        this.errorMessage = error.message
      }
    },
    closeDialogSaveTemplate() {
      this.dialogTemplateSaveIsOpen = false
      this.$nextTick(() => {
        this.editedItemTemplate = _.cloneDeep(this.defaultItemTemplate)
      })
      this.tabCurrentRename = ''
      this.errorMessage = ''
    },
    confirmDeleteTemplate(template) {
      const { id, name } = template

      this.closeDialogTemplate()

      this.dialogConfirm.action = 'removeTemplate'
      this.dialogConfirm.open = true
      this.dialogConfirm.id = id
      this.dialogConfirm.name = name
      this.dialogConfirm.message = `Delete <b>${name}</b>?`
    },
    async deleteTemplate(id, name) {
      try {
        await apiFlowTemplate.deleteFlowTemplate(id)

        this.snackbar.textSnackbar = `${name} template is deleted`
        this.snackbar.colorSnackbar = 'error'
        this.snackbar.open = true
      } catch {}
    },
    closeDialogTemplate() {
      this.dialogTemplate = {
        open: false,
        data: {}
      }

      this.errorMessage = ''
    },
    // end flow template

    callToFlow() {
      const isDirty = this.isEnableUndo
      if (isDirty) {
        this.dialogCall.errorMessage =
          'Note: You have unsaved changes. This will call to the last saved version instead.'
      }
      this.dialogCall._id = this.flowId
      this.dialogCall.name = this.flowName
      this.dialogCall.open = true
    },
    // end call to flow

    copyNode() {
      if (this.isLocked) return

      this.copyNodes = { page: this.tabSelected, elements: {} }

      for (const id of this.nodesSelected) {
        const node = this.editor.getNodeFromId(id)

        if (!['start', 'enter'].includes(node.data.node))
          this.copyNodes.elements[id] = node
      }

      this.snackbar.textSnackbar = `${
        Object.keys(this.copyNodes.elements).length
      } components copied`
      this.snackbar.colorSnackbar = 'success'
      this.snackbar.open = true
    },
    parseNode() {
      if (this.isLocked) return

      if (Object.keys(this.copyNodes.elements).length === 0) return

      const origin = JSON.parse(JSON.stringify(this.editor.export()))
      const copyNode = JSON.parse(JSON.stringify(this.copyNodes.elements))

      const arrange = new Arrange()

      // setup new position
      const position = { x: 0, y: 0 }

      const { width, height } = this.editor.container.getBoundingClientRect()

      const x = width / 2
      const y = height / 2

      position.x =
        x *
          (this.editor.precanvas.clientWidth /
            (this.editor.precanvas.clientWidth * this.editor.zoom)) -
        this.editor.precanvas.getBoundingClientRect().x *
          (this.editor.precanvas.clientWidth /
            (this.editor.precanvas.clientWidth * this.editor.zoom))
      position.y =
        y *
          (this.editor.precanvas.clientHeight /
            (this.editor.precanvas.clientHeight * this.editor.zoom)) -
        this.editor.precanvas.getBoundingClientRect().y *
          (this.editor.precanvas.clientHeight /
            (this.editor.precanvas.clientHeight * this.editor.zoom))

      const newPositionCopyNode = arrange.adjustPosition(position, copyNode)

      let newTemplate = arrange.rearrangeComponent(
        origin,
        newPositionCopyNode,
        { from: this.copyNodes.page, to: this.tabSelected }
      )
      newTemplate = arrange.renameComponent(origin, newTemplate)
      newTemplate = arrange.checkInputOutput(newTemplate)

      const tmpTemplate = JSON.parse(JSON.stringify(newTemplate))

      newTemplate = {
        ...origin.drawflow[this.editor.module].data,
        ...newTemplate
      }

      origin.drawflow[this.editor.module].data = newTemplate

      this.editor.import(origin)

      this.getAllComponentItemsInTabs()
      this.pushFlowHistory()

      // auto selection
      this.nodesSelected = []
      Object.keys(tmpTemplate).forEach(id => {
        const node = document.getElementById(`node-${id}`)
        if (node) {
          node.classList.add('selected')
          this.nodesSelected.push(id)
        }
      })
    },

    setPageLock(state) {
      this.isLocked = state

      const index = _.findIndex(this.tabs, { name: this.tabSelected })
      this.tabs[index].locked = this.isLocked

      this.editor.editor_mode = state ? 'fixed' : 'edit'
    },

    showConversation() {
      if (this.reload) clearInterval(this.reload)

      setTimeout(() => {
        const panelRight = document.getElementById('panel-right')
        if (panelRight.offsetWidth < 650) {
          panelRight.style.width = '650px'
        }
      }, 100)

      this.dialConversation.isOpen = ''

      setTimeout(async () => {
        const getCaller = await apiConversation.getCaller({
          filter: { qc: {} },
          query: { bot_session_id: this.dialConversation.botSessionId },
          sortBy: ['created_at'],
          sortDesc: [true]
        })

        const { docs, totalPages, totalDocs } = getCaller.data

        if (totalDocs > 0) {
          const i = 0

          const obj = {}
          obj.created_at = docs[i].created_at
          obj.customer = docs[i].new_customer
          obj.platformId =
            docs[i].customers && docs[i].customers.profile
              ? docs[i].customers.profile.platform_id
              : ''
          obj.call_type = docs[i].channel
          obj.channel = docs[i].new_channel
          obj.duration = docs[i].duration || ''
          obj.status = docs[i].action
          obj.qc_by = docs[i].qc_by
          obj.should_discuss = docs[i].should_discuss
          obj.direction = docs[i].direction || 'inbound'
          obj._id = docs[i]._id || ''
          obj.conversations = docs[i].conversations || []
          obj.platform = docs[i].channel || []
          obj.all = docs[i]

          this.dialConversation.chatCaller = obj

          this.getConversation()
        }
      }, 2000)
    },
    async getConversation() {
      if (this.reload) clearInterval(this.reload)

      const data = this.dialConversation.chatCaller

      const getChatCaller = await apiConversation.getConversation(data.all._id)
      const chatConversations = {
        session_id: data.all._id,
        conversations: getChatCaller.data,
        action: getChatCaller.session.action,
        is_end_session: getChatCaller.session.is_end_session,
        duration: getChatCaller.session.duration,
        index_row: 0,
        channel: data.all.channel,
        customers: getChatCaller.customer,
        wrap_up: data.all.wrap_up,
        call_recording_url: data.all.call_recording_url,
        created_at: data.all.created_at
      }

      this.dialConversation.chatDetail = chatConversations

      if (chatConversations.action === 'on call')
        this.reload = setInterval(() => {
          this.getConversation()

          const element1 = document.getElementById('chat-container')
          element1.scrollTop = element1.scrollHeight - element1.clientHeight
        }, 1000)
    },
    hideConversation() {
      if (this.reload) clearInterval(this.reload)

      this.dialConversation = {
        isOpen: false,
        botSessionId: '',
        chatCaller: {},
        chatDetail: {},
        reload: null
      }
      setTimeout(() => {
        const panelRight = document.getElementById('panel-right')
        panelRight.style.width = '0px'

        const panelLeft = document.getElementById('panel-left')
        panelLeft.style.width = '100%'
      }, 100)
    },
    checkSavedFlow() {
      const obj = {
        tabs: this.tabs,
        structure: this.structure,
        ui: this.editor.export()
      }

      if (_.isEqual(obj, this.tempFlowUi) || !this.user.auth) return true
      return false
    },
    beforeWindowUnload(e) {
      if (!this.checkSavedFlow()) {
        e.preventDefault()
        e.returnValue = ''
      }
    },
    saveFlowToTemp() {
      this.tempFlowUi = {
        tabs: this.tabs,
        structure: this.structure,
        ui: this.editor.export()
      }

      this.isUnsavedFlow = false
    },
    searchStructure(id, text) {},
    setupStructure(data) {
      this.structure = data
    },

    // import && export
    async importFlow(obj) {
      try {
        if (obj) {
          const { data } = obj

          if (!data.tabs || !data.ui) {
            this.errorMessage = 'The format is not supported.'
            return false
          }

          this.tabs = data.tabs
          this.pageClick('Home', { noPushPage: false })
          this.clearPageHistory()
          this.toggleBackwardForward()

          const arrange = new Arrange()
          const { tabs, ui } = await arrange.checkUiAvailable({
            tabs: data.tabs,
            ui: data.ui,
            platform: data.platform
          })
          this.tabs = tabs
          this.structure = this.collapseAllFolder(data.structure)

          this.editor.import(ui)

          this.getAllComponentItemsInTabs()
          // init Home flow history
          this.flowHistory[this.tabs[0].name][0] = data.ui

          // set position when start home
          const { x, y } = this.tabs[0].position
            ? this.tabs[0].position
            : { x: 0, y: 0 }
          this.setCanvas(x, y)

          this.setPageLock(this.tabs[0].locked || false)
        }
        this.dialogImportIsOpen = false
      } catch (error) {
        this.errorMessage = error.message
      }
    },
    async exportFlow() {
      try {
        const flowUi = await apiFlow.getFlowUi(this.flowId)
        if (flowUi && flowUi.data) {
          const { name, platform, flow_ui } = flowUi.data

          const ui = JSON.parse(flow_ui)
          const obj = {
            platform: platform,
            tabs: ui.tabs,
            structure: ui.structure,
            ui: ui.ui
          }
          const data = JSON.stringify(obj)
          const blob = new Blob([data], { type: 'text/json' })
          const e = document.createEvent('MouseEvents')
          const a = document.createElement('a')

          a.download = `${name} - ${moment().format('YYYY-MM-DD_HHmm')}.json`
          a.href = window.URL.createObjectURL(blob)
          a.dataset.downloadurl = ['text/json', a.download, a.href].join(':')
          e.initEvent(
            'click',
            true,
            false,
            window,
            0,
            0,
            0,
            0,
            0,
            false,
            false,
            false,
            false,
            0,
            null
          )
          a.dispatchEvent(e)
        }
      } catch {}
    }
  }
}
</script>

<style lang="scss" scoped>
#icon-dots {
  margin-left: 5px;
  font-size: 22px !important;
}

.pointer {
  cursor: pointer;
}

::v-deep .search-input {
  background-color: white !important;
  box-shadow: 0px 0px 0px 1px #ebebeb;
  height: 30px !important;
  margin: 0;
  // margin-bottom: 20px;
  line-height: 10px;

  .v-text-field__slot {
    margin-top: 0px;
  }

  &.v-input--is-focused {
    box-shadow: 0px 0px 0px 2px $primary !important;
  }

  .v-input__icon--prepend-inner {
    margin-top: 2px !important;
  }

  .v-input__icon--clear {
    margin-top: -5px !important;
  }

  .v-label {
    top: 4px !important;
  }

  input {
    background-color: white !important;
  }
}
.background-white {
  background-color: white !important;
}

.list-height {
  max-height: 350px;
  overflow-y: auto;
}

.no-page {
  text-align: center;
  padding: 10px 0;
}

.custom-resizer {
  height: calc(100vh - 50px) !important;

  #panel-left {
    min-width: 740px;
  }
}

.drawflow-custom-resizer {
  height: calc(100vh - 125px) !important;
  overflow: hidden !important;

  #panel-drawflow-right {
    min-width: calc(100% - 350px) !important;
  }

  #panel-drawflow-left {
    max-width: 350px;
  }
}

.container-page {
  padding: 0px !important;
}

::v-deep .custom-resizer .innerContent {
  height: calc(100vh - 80px) !important;
}

::v-deep .drawflow-custom-resizer .innerContent {
  height: 100% !important;
}

::v-deep .drawflow-custom-resizer #panel-drawflow-right .v-resizer {
  &:hover {
    &:before {
      cursor: col-resize !important;
    }
  }
}

.link {
  text-decoration: none; /* Remove underline */
}

.border {
  background-color: $primary !important;
  color: white;
  padding-left: 5px;
  height: 25px;
}
</style>
